import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Skeleton,
  Alert,
  Grid,
  Input,
  Select,
  Option,
  IconButton,
  FormControl,
  FormLabel,
  Table,
  Sheet,
  Modal,
  ModalDialog,
  ModalClose,
  Divider,
  Snackbar,
  Tooltip,
  DialogTitle,
  DialogContent,
} from '@mui/joy';
import { Container } from '@mui/material';
import NativeCheckbox from '../../main/components/NativeCheckbox';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DirectionalBackIcon from '../../main/components/DirectionalBackIcon';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ShipmentInvoice from '../../shipments_manager/views/shipment_invoice_screen';
import { shipmentTotal } from '../../shipments_manager/views/htmlDocumentUtils';
import { useAuth } from '../../main/context/AuthContext';
import { getCustomerInvoices, exportCustomerInvoicesToExcel, regenerateCustomerInvoices } from '../../main/API/customer';
import { useNavigate } from 'react-router-dom';
import { formatPaymentStatus, getPaymentStatusChipStyle, getPaymentStatusIconConfig } from '../../main/widgets/PaymentStatusOptionList';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { OmrAmount } from '../../main/components/OmrAmount';
import { translateApiErrorData } from '../../main/utils/masterApiError';
import { dedupeRequest } from '../../main/utils/dedupeRequest';
import CustomerInvoicePayActions from './widgets/CustomerInvoicePayActions';
import { getPaginatedMeta } from '../../main/utils/pagination';
import {
  formatCustomerShipmentUnitDate,
  getCustomerShipmentUnit,
  getCustomerShipmentUnitSerial,
  isCustomerShipmentUnitFieldVisible,
  useCustomerShipmentUnitVisibleFields,
} from '../utils/customerShipmentUnit';

function formatDate(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (_) {
    return '—';
  }
}

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '—';
  }
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const initialFilters = {
  id_in: '',
  note: '',
  payment_status: '',
  paid: '',
  created_at_after: '',
  created_at_before: '',
  modified_at_after: '',
  modified_at_before: '',
  shipment_in: '',
  ordering: '-created_at',
};

function toIsoDateOnly(dateStr) {
  if (!dateStr || !dateStr.trim()) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

/**
 * True if invoice is eligible for regeneration (same as API).
 * Eligible when: paid = false, and (payment_link empty, or payment_link_expire_at passed,
 * or payment link is Thawani checkout with expire_at null).
 */
function needsRegenerate(inv) {
  if (!inv) return false;
  const link = inv.payment_link != null ? String(inv.payment_link).trim() : '';
  if (!link) return true;
  const expireAt = inv.payment_link_expire_at;
  // Thawani checkout links with no expiry: show Regenerate (e.g. checkout.thawani.om)
  if (link.includes('checkout.thawani') && (expireAt == null || expireAt === '')) return true;
  if (!expireAt) return false;
  const d = new Date(expireAt);
  return !Number.isNaN(d.getTime()) && d.getTime() < Date.now();
}

export default function CustomerMyInvoicesScreen() {
  const { t, i18n } = useTranslation('ui');
  const { user, logout } = useAuth();
  const tRef = useRef(t);
  const logoutRef = useRef(logout);
  tRef.current = t;
  logoutRef.current = logout;
  const accountInactive = Boolean(user && user.is_active === false);
  const unitVisibleFields = useCustomerShipmentUnitVisibleFields();
  const navigate = useNavigate();

  const paymentStatusFilterOptions = useMemo(
    () => [
      { value: '', label: t('customerList.paymentAny') },
      { value: 'unpaid', label: t('customerList.paymentUnpaid') },
      { value: 'partially_paid', label: t('customerList.paymentPartiallyPaid') },
      { value: 'paid', label: t('customerList.paymentPaid') },
      { value: 'overpaid', label: t('customerList.paymentOverpaid') },
      { value: 'refunded', label: t('customerList.paymentRefunded') },
    ],
    [t]
  );

  const booleanFilterOptions = useMemo(
    () => [
      { value: '', label: t('customerList.boolAny') },
      { value: 'true', label: t('customerList.boolYes') },
      { value: 'false', label: t('customerList.boolNo') },
    ],
    [t]
  );

  const orderingOptions = useMemo(
    () => [
      { value: '-created_at', label: t('customerList.orderNewestFirst') },
      { value: 'created_at', label: t('customerList.orderOldestFirst') },
      { value: '-modified_at', label: t('customerList.orderRecentlyUpdated') },
      { value: 'modified_at', label: t('customerList.orderLeastRecentlyUpdated') },
      { value: '-id', label: t('customerList.orderIdHighToLow') },
      { value: 'id', label: t('customerList.orderIdLowToHigh') },
      { value: 'payment_status', label: t('customerList.orderPaymentStatusAZ') },
      { value: '-payment_status', label: t('customerList.orderPaymentStatusZA') },
      { value: 'paid', label: t('customerList.orderPaidFirst') },
      { value: '-paid', label: t('customerList.orderUnpaidFirst') },
    ],
    [t]
  );
  const [data, setData] = useState({ results: [], count: 0, page_index: 1, page_size: 10 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [quickViewInvoice, setQuickViewInvoice] = useState(null);
  const [pdfInvoice, setPdfInvoice] = useState(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportSuccessMessage, setExportSuccessMessage] = useState(null);
  const [regeneratingIds, setRegeneratingIds] = useState(new Set());
  const [regenerateSnackbar, setRegenerateSnackbar] = useState({ open: false, message: '', color: 'neutral' });
  const [openPaymentLinkAfterRefresh, setOpenPaymentLinkAfterRefresh] = useState(null);

  const buildParams = useCallback(() => {
    const p = { page, page_size: pageSize, ordering: appliedFilters.ordering };
    [
      'id_in', 'note', 'payment_status', 'paid',
      'created_at_after', 'created_at_before', 'modified_at_after', 'modified_at_before',
      'shipment_in',
    ].forEach((key) => {
      const v = appliedFilters[key];
      if (v !== undefined && v !== null && String(v).trim() !== '') {
        if (key === 'created_at_after' || key === 'created_at_before' || key === 'modified_at_after' || key === 'modified_at_before') {
          const dateVal = toIsoDateOnly(v);
          if (dateVal) p[key] = dateVal;
        } else {
          p[key] = v;
        }
      }
    });
    return p;
  }, [page, pageSize, appliedFilters]);

  const listRequestKey = `customer/invoices:${page}:${pageSize}:${JSON.stringify(appliedFilters)}`;

  const fetchInvoices = useCallback(async ({ dedupeKey, isCancelled } = {}) => {
    if (isCancelled && isCancelled()) return;
    if (accountInactive) {
      setLoading(false);
      setData({ results: [], count: 0, page_index: 1, page_size: pageSize });
      setError(null);
      return;
    }
    setError(null);
    setLoading(true);
    const params = buildParams();
    const doFetch = () => getCustomerInvoices(params);
    const response = dedupeKey
      ? await dedupeRequest(dedupeKey, doFetch)
      : await doFetch();
    if (isCancelled && isCancelled()) return;
    setLoading(false);

    if (!response) {
      setError(tRef.current('customerList.loadInvoicesError'));
      return;
    }
    if (response.status === 401) {
      logoutRef.current();
      return;
    }
    if (response.status >= 400) {
      setError((response.data && translateApiErrorData(response.data, tRef.current)) || tRef.current('customerList.loadInvoicesFailed'));
      setData({ results: [], count: 0, page_index: 1, page_size: pageSize });
      return;
    }

    const body = response.data;
    setData(getPaginatedMeta(body, pageSize));
  }, [accountInactive, buildParams, pageSize]);

  const fetchInvoicesRef = useRef(fetchInvoices);
  fetchInvoicesRef.current = fetchInvoices;

  useEffect(() => {
    let cancelled = false;
    fetchInvoicesRef.current({ dedupeKey: listRequestKey, isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listRequestKey, accountInactive]);

  // After regenerate, open the new payment link in a new tab when data has refreshed
  useEffect(() => {
    if (openPaymentLinkAfterRefresh == null || !data.results.length) return;
    const inv = data.results.find((r) => r.id === openPaymentLinkAfterRefresh);
    // Prefer shared invoice.payment_link only — never substitute a separate-group URL.
    const link = inv && inv.payment_link && String(inv.payment_link).trim();
    if (link) {
      window.open(link, '_blank', 'noopener,noreferrer');
      setOpenPaymentLinkAfterRefresh(null);
    }
  }, [data.results, openPaymentLinkAfterRefresh]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(1);
  };

  const hasActiveFilters = Object.keys(initialFilters).some(
    (k) => k !== 'ordering' && appliedFilters[k] !== undefined && appliedFilters[k] !== null && String(appliedFilters[k]).trim() !== ''
  );
  const clearAllFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  };

  const pageIds = (data.results || []).map((inv) => inv.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedInvoiceIds.has(id));
  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedInvoiceIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedInvoiceIds((prev) => {
        const next = new Set(prev);
        pageIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };
  const toggleSelectInvoice = (id) => {
    setSelectedInvoiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleExportToExcel = async () => {
    setExportError(null);
    setExportLoading(true);
    try {
      const hasSelection = selectedInvoiceIds.size > 0;
      const params = hasSelection
        ? { invoice_ids: Array.from(selectedInvoiceIds) }
        : buildParams();
      const response = await exportCustomerInvoicesToExcel(params);
      setExportLoading(false);
      if (!response) {
        setExportError(t('customerList.exportFailed'));
        return;
      }
      if (response.status === 401) {
        logout();
        return;
      }
      if (response.status >= 400) {
        let msg = t('customerList.exportFailedShort');
        if (response.data instanceof Blob) {
          try {
            const text = await response.data.text();
            const json = JSON.parse(text);
            msg = translateApiErrorData(json, t) || msg;
          } catch (_) {
            // keep default msg
          }
        } else if (response.data) {
          msg = translateApiErrorData(response.data, t) || msg;
        }
        setExportError(msg);
        return;
      }
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_');
      link.download = `my_invoices_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      setExportError(null);
      setExportSuccessMessage(t('customerList.exportSuccess'));
    } catch (err) {
      setExportLoading(false);
      setExportError(err.message || t('customerList.exportFailedShort'));
    }
  };

  const handleRegenerate = async (invoiceId) => {
    if (!invoiceId) return;
    setRegeneratingIds((prev) => new Set(prev).add(invoiceId));
    setRegenerateSnackbar({ open: false });
    const response = await regenerateCustomerInvoices([invoiceId]);
    setRegeneratingIds((prev) => {
      const next = new Set(prev);
      next.delete(invoiceId);
      return next;
    });
    if (!response) {
      setRegenerateSnackbar({ open: true, message: t('customer.paymentLinkError'), color: 'danger' });
      return;
    }
    if (response.status === 401) {
      logout();
      return;
    }
    if (response.status >= 400) {
      const msg =
        (response.data && translateApiErrorData(response.data, t)) || t('customer.paymentLinkFailed');
      setRegenerateSnackbar({ open: true, message: msg, color: 'danger' });
      return;
    }
    const count = response.data && response.data.regenerated_invoice_count != null ? response.data.regenerated_invoice_count : 0;
    setRegenerateSnackbar({
      open: true,
      message: count > 0 ? t('customer.invoicesUpdated', { count }) : t('customer.noNewPaymentLinks'),
      color: 'success',
    });
    setQuickViewInvoice(null);
    fetchInvoices();
  };

  const totalPages = Math.max(1, Math.ceil((data.count || 0) / pageSize));
  const pageBg = '#f0f4f8';
  const cardBg = '#ffffff';
  const cardBorder = '1px solid #e2e8f0';
  const cardShadow = '0 2px 8px rgba(0,0,0,0.06)';
  const cardRadius = '16px';
  const textPrimary = '#1e293b';
  const textSecondary = '#64748b';
  const textMuted = '#94a3b8';

  if (loading && !data.results.length) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
        <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
          <Container maxWidth="xl">
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: cardRadius, mb: 3, bgcolor: cardBg }} />
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: cardRadius, mb: 2, bgcolor: cardBg }} />
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid key={i} xs={12} sm={6} md={4}>
                  <Skeleton variant="rectangular" height={180} sx={{ borderRadius: cardRadius, bgcolor: cardBg }} />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: pageBg }}>
      <Box component="main" sx={{ overflow: 'auto', pt: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="plain"
                size="sm"
                startDecorator={<DirectionalBackIcon />}
                onClick={() => navigate('/customer-home')}
                sx={{ color: textSecondary, fontWeight: 500, mr: 0.5 }}
              >
                {t('customerList.homeNav')}
              </Button>
              <Box sx={{ width: 44, height: 44, borderRadius: '12px', bgcolor: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ReceiptIcon sx={{ color: '#059669', fontSize: 22 }} />
              </Box>
              <Typography level="h4" sx={{ fontWeight: 700, color: textPrimary }}>
                {t('customerList.myInvoicesTitle')}
              </Typography>
            </Box>
            <Button
              variant="soft"
              size="sm"
              startDecorator={<RefreshIcon />}
              onClick={fetchInvoices}
              loading={loading}
              sx={{ borderRadius: '10px', fontWeight: 500 }}
            >
              {t('actions.refresh')}
            </Button>
          </Box>

          {error && (
            <Alert
              color="danger"
              variant="soft"
              sx={{ mb: 3, borderRadius: '12px' }}
              endDecorator={
                <Button variant="soft" color="danger" size="sm" onClick={fetchInvoices}>
                  {t('actions.retry')}
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          <Card variant="plain" sx={{ bgcolor: cardBg, boxShadow: cardShadow, borderRadius: cardRadius, border: cardBorder, mb: 3 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end', mb: showAllFilters ? 2 : 0 }}>
                <Select
                  size="md"
                  value={filters.payment_status}
                  onChange={(_, v) => updateFilter('payment_status', v || '')}
                  sx={{ minWidth: 160, borderRadius: '10px' }}
                  renderValue={(option) => {
                    if (!filters.payment_status) return (option && option.label) || t('customerList.paymentAny');
                    const config = getPaymentStatusIconConfig(filters.payment_status);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MonetizationOnIcon
                          color={config.color === 'success' ? 'success' : undefined}
                          sx={config.color !== 'success' ? { color: config.color, fontSize: '18px' } : { fontSize: '18px' }}
                        />
                        <span>{config.title}</span>
                      </Box>
                    );
                  }}
                >
                  {paymentStatusFilterOptions.map((opt) => {
                    if (!opt.value) {
                      return <Option key="any" value={opt.value}>{opt.label}</Option>;
                    }
                    const config = getPaymentStatusIconConfig(opt.value);
                    return (
                      <Option key={opt.value} value={opt.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MonetizationOnIcon
                            color={config.color === 'success' ? 'success' : undefined}
                            sx={config.color !== 'success' ? { color: config.color, fontSize: '18px' } : { fontSize: '18px' }}
                          />
                          <span>{config.title}</span>
                        </Box>
                      </Option>
                    );
                  })}
                </Select>
                <Select
                  size="md"
                  value={filters.ordering}
                  onChange={(_, v) => updateFilter('ordering', v || '-created_at')}
                  sx={{ minWidth: 160, borderRadius: '10px' }}
                >
                  {orderingOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
                <Button size="md" onClick={handleApplyFilters} startDecorator={<SearchIcon />} sx={{ borderRadius: '10px' }}>
                  {t('actions.apply')}
                </Button>
                {hasActiveFilters && (
                  <Button variant="plain" size="md" onClick={clearAllFilters}>
                    {t('customerList.clearAll')}
                  </Button>
                )}
                <Button
                  variant="soft"
                  size="md"
                  color="neutral"
                  onClick={() => setShowAllFilters((s) => !s)}
                  endDecorator={showAllFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  startDecorator={<FilterListIcon />}
                  sx={{ borderRadius: '10px' }}
                >
                  {showAllFilters ? t('customerList.lessFilters') : t('customerList.allFilters')}
                </Button>
                <Button
                  variant="soft"
                  size="md"
                  startDecorator={<FileDownloadIcon />}
                  onClick={handleExportToExcel}
                  loading={exportLoading}
                  disabled={exportLoading}
                  sx={{ borderRadius: '10px', fontWeight: 500 }}
                >
                  {selectedInvoiceIds.size > 0
                    ? t('customerList.exportExcelSelected', { count: selectedInvoiceIds.size })
                    : t('customerList.exportExcel')}
                </Button>
              </Box>

              {showAllFilters && (
                <Box sx={{ pt: 2, borderTop: '1px solid #e2e8f0' }}>
                  <Grid container spacing={2}>
                    <Grid xs={12}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.identity')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.invoiceIdsComma')}</FormLabel>
                        <Input
                          placeholder={t('customerList.idInPlaceholder')}
                          value={filters.id_in}
                          onChange={(e) => updateFilter('id_in', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sx={{ mt: 1 }}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.invoicePayment')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.noteContains')}</FormLabel>
                        <Input
                          placeholder={t('customerList.notePlaceholder')}
                          value={filters.note}
                          onChange={(e) => updateFilter('note', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('widgets.paymentStatus')}</FormLabel>
                        <Select
                          value={filters.payment_status}
                          onChange={(_, v) => updateFilter('payment_status', v || '')}
                          sx={{ borderRadius: '8px' }}
                        >
                          {paymentStatusFilterOptions.map((opt) => (
                            <Option key={opt.value || 'any'} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={2}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.paidLabel')}</FormLabel>
                        <Select
                          value={filters.paid}
                          onChange={(_, v) => updateFilter('paid', v || '')}
                          sx={{ borderRadius: '8px' }}
                        >
                          {booleanFilterOptions.map((opt) => (
                            <Option key={opt.value || 'any'} value={opt.value}>{opt.label}</Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sx={{ mt: 1 }}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.shipmentsFilter')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={4}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.shipmentIdsComma')}</FormLabel>
                        <Input
                          placeholder={t('customerList.idInPlaceholder')}
                          value={filters.shipment_in}
                          onChange={(e) => updateFilter('shipment_in', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sx={{ mt: 1 }}>
                      <Typography level="title-sm" sx={{ color: textSecondary, mb: 1 }}>{t('customerList.dateRange')}</Typography>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.createdAfter')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.created_at_after}
                          onChange={(e) => updateFilter('created_at_after', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.createdBefore')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.created_at_before}
                          onChange={(e) => updateFilter('created_at_before', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.modifiedAfter')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.modified_at_after}
                          onChange={(e) => updateFilter('modified_at_after', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                    <Grid xs={12} sm={6} md={3}>
                      <FormControl size="sm">
                        <FormLabel>{t('customerList.modifiedBefore')}</FormLabel>
                        <Input
                          type="date"
                          value={filters.modified_at_before}
                          onChange={(e) => updateFilter('modified_at_before', e.target.value)}
                          sx={{ borderRadius: '8px' }}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    <Button size="sm" onClick={handleApplyFilters}>
                      {t('customerList.applyFilters')}
                    </Button>
                    <Button variant="plain" size="sm" onClick={clearAllFilters}>
                      {t('customerList.clearAll')}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {selectedInvoiceIds.size > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mb: 2,
                p: 1.5,
                borderRadius: '12px',
                bgcolor: '#ecfdf5',
                border: '1px solid #a7f3d0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography level="body1" sx={{ fontWeight: 600, color: textPrimary }}>
                  {t('customerList.invoicesSelected', { count: selectedInvoiceIds.size })}
                </Typography>
                <Button
                  variant="plain"
                  size="sm"
                  onClick={() => setSelectedInvoiceIds(new Set())}
                  sx={{ fontWeight: 500 }}
                >
                  {t('customerList.clearSelection')}
                </Button>
              </Box>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography level="body2" sx={{ color: textSecondary }}>
              {data.count === 0 ? t('customerList.noInvoices') : t('customerList.totalInvoices', { count: data.count })}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="body2" sx={{ color: textSecondary }}>{t('customerList.perPage')}</Typography>
              <Select
                size="sm"
                value={pageSize}
                onChange={(_, v) => {
                  setPageSize(v || 10);
                  setPage(1);
                }}
                sx={{ width: 70 }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <Option key={n} value={n}>{n}</Option>
                ))}
              </Select>
            </Box>
          </Box>

          {!data.results.length ? (
            <Card variant="plain" sx={{ bgcolor: cardBg, borderRadius: cardRadius, boxShadow: cardShadow, border: cardBorder }}>
              <CardContent sx={{ py: 6, px: 2, textAlign: 'center' }}>
                <ReceiptIcon sx={{ fontSize: 48, color: textMuted, opacity: 0.6, mb: 1.5 }} />
                <Typography level="body1" sx={{ color: textSecondary }}>
                  {t('customerList.noInvoicesFound')}
                </Typography>
                {hasActiveFilters && (
                  <Button variant="soft" size="sm" sx={{ mt: 2 }} onClick={clearAllFilters}>
                    {t('customerList.clearFiltersBtn')}
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <>
              <Sheet
                variant="outlined"
                sx={{
                  borderRadius: cardRadius,
                  border: cardBorder,
                  boxShadow: cardShadow,
                  overflow: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  '& thead th': { fontWeight: 600, color: textSecondary, fontSize: '0.8125rem', whiteSpace: 'nowrap' },
                  '& tbody tr:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  '& td': { verticalAlign: 'middle', fontSize: '0.875rem' },
                  '& thead th:first-of-type': {
                    position: 'sticky',
                    top: 0,
                    insetInlineStart: 0,
                    zIndex: 12,
                    minWidth: 48,
                    width: 48,
                    backgroundColor: cardBg,
                    backgroundClip: 'padding-box',
                    boxShadow: '4px 0 8px rgba(0,0,0,0.08)',
                    pointerEvents: 'auto',
                    isolation: 'isolate',
                  },
                  'html[dir="rtl"] & thead th:first-of-type': {
                    boxShadow: '-4px 0 8px rgba(0,0,0,0.08)',
                  },
                  '& tbody td:first-of-type': {
                    position: 'sticky',
                    insetInlineStart: 0,
                    zIndex: 11,
                    backgroundColor: cardBg,
                    backgroundClip: 'padding-box',
                    boxShadow: '4px 0 8px rgba(0,0,0,0.06)',
                    pointerEvents: 'auto',
                    isolation: 'isolate',
                  },
                  'html[dir="rtl"] & tbody td:first-of-type': {
                    boxShadow: '-4px 0 8px rgba(0,0,0,0.06)',
                  },
                  '& tbody tr:hover td:first-of-type': {
                    backgroundColor: 'rgba(0,0,0,0.04)',
                  },
                }}
              >
                <Table stickyHeader hoverRow size="md" sx={{ minWidth: 1160, borderCollapse: 'separate', borderSpacing: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 48 }}>
                        {data.results.length > 0 && (
                          <NativeCheckbox
                            checked={allOnPageSelected}
                            indeterminate={selectedInvoiceIds.size > 0 && !allOnPageSelected}
                            onChange={toggleSelectAll}
                            sx={{ verticalAlign: 'middle' }}
                          />
                        )}
                      </th>
                      <th style={{ minWidth: 72, width: 72 }}>{t('customerList.colId')}</th>
                      <th style={{ minWidth: 140 }}>{t('customerList.colNote')}</th>
                      <th style={{ minWidth: 110 }}>{t('customerList.colShipments')}</th>
                      <th style={{ minWidth: 100 }}>{t('customerList.colTotalPrice')}</th>
                      <th style={{ minWidth: 125 }}>{t('customerList.colPayment')}</th>
                      <th style={{ minWidth: 88 }}>{t('customerList.colPaid')}</th>
                      <th style={{ minWidth: 115 }}>{t('customerList.colCreated')}</th>
                      <th style={{ minWidth: 115 }}>{t('customerList.colModified')}</th>
                      <th style={{ minWidth: 160, whiteSpace: 'nowrap' }}>{t('customerList.colAction')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.results.map((inv) => (
                      <tr key={inv.id}>
                        <td>
                          <NativeCheckbox
                            checked={selectedInvoiceIds.has(inv.id)}
                            onChange={() => toggleSelectInvoice(inv.id)}
                            sx={{ verticalAlign: 'middle' }}
                          />
                        </td>
                        <td>
                          <Typography level="body2" sx={{ fontWeight: 600, color: textPrimary }}>
                            #{inv.id}
                          </Typography>
                        </td>
                        <td>
                          <Typography level="body2" sx={{ maxWidth: 180 }} noWrap title={inv.note || ''}>
                            {inv.note || '—'}
                          </Typography>
                        </td>
                        <td>
                          {inv.shipments && inv.shipments.length > 0 ? (
                            <Chip size="sm" variant="soft" sx={{ fontWeight: 500 }}>
                              {t('customerList.shipmentCount', { count: inv.shipments.length })}
                            </Chip>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td>
                          {shipmentTotal(inv.shipments) > 0 ? (
                            <Typography level="body2" sx={{ fontWeight: 600, color: textPrimary }}>
                              <OmrAmount value={shipmentTotal(inv.shipments)} />
                            </Typography>
                          ) : '—'}
                        </td>
                        <td>
                          {inv.payment_status ? (
                            <Chip
                              size="sm"
                              variant="soft"
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                ...getPaymentStatusChipStyle(inv.payment_status),
                              }}
                            >
                              {formatPaymentStatus(inv.payment_status)}
                            </Chip>
                          ) : '—'}
                        </td>
                        <td>{inv.paid != null ? (inv.paid ? t('actions.yes') : t('actions.no')) : '—'}</td>
                        <td>{formatDate(inv.created_at)}</td>
                        <td>{formatDate(inv.modified_at)}</td>
                        <td>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'nowrap' }}>
                            <Tooltip title={t('invoiceManager.tooltipInvoicePdf')}>
                              <IconButton
                                variant="soft"
                                size="sm"
                                color="neutral"
                                onClick={() => setPdfInvoice(inv)}
                                sx={{ borderRadius: '8px' }}
                              >
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('customer.quickViewButtonTitle')}>
                              <IconButton
                                variant="soft"
                                size="sm"
                                color="neutral"
                                onClick={() => setQuickViewInvoice(inv)}
                                sx={{ borderRadius: '8px' }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <CustomerInvoicePayActions
                              invoice={inv}
                              needsRegenerate={needsRegenerate}
                              regenerating={regeneratingIds.has(inv.id)}
                              onRegenerate={() => {
                                setOpenPaymentLinkAfterRefresh(inv.id);
                                handleRegenerate(inv.id);
                              }}
                              onAfterPayFull={async () => {
                                await fetchInvoices();
                              }}
                              iconOnly
                            />
                          </Box>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>

              <Modal open={!!quickViewInvoice} onClose={() => setQuickViewInvoice(null)}>
                <ModalDialog
                  sx={{
                    maxWidth: 560,
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    borderRadius: cardRadius,
                    p: 2.5,
                  }}
                >
                  <ModalClose />
                  {quickViewInvoice && (
                    <>
                      <Typography level="h5" sx={{ mb: 2, fontWeight: 700 }}>
                        {t('customer.invoiceNumber', { id: quickViewInvoice.id })}
                      </Typography>
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {[
                          { fieldKey: 'note', value: quickViewInvoice.note || '—' },
                          {
                            fieldKey: 'invoiceTotal',
                            value: shipmentTotal(quickViewInvoice.shipments) > 0
                              ? <OmrAmount value={shipmentTotal(quickViewInvoice.shipments)} />
                              : '—',
                          },
                          {
                            fieldKey: 'amountPaid',
                            value: quickViewInvoice.amount_paid != null
                              ? <OmrAmount value={quickViewInvoice.amount_paid} />
                              : '—',
                          },
                          {
                            fieldKey: 'amountDue',
                            value: quickViewInvoice.amount_due != null
                              ? <OmrAmount value={quickViewInvoice.amount_due} />
                              : '—',
                          },
                          { fieldKey: 'paymentStatus', value: quickViewInvoice.payment_status ? formatPaymentStatus(quickViewInvoice.payment_status) : '—', variant: 'paymentStatus' },
                          { fieldKey: 'paid', value: quickViewInvoice.paid != null ? (quickViewInvoice.paid ? t('actions.yes') : t('actions.no')) : '—' },
                          { fieldKey: 'created', value: formatDateTime(quickViewInvoice.created_at) },
                          { fieldKey: 'modified', value: formatDateTime(quickViewInvoice.modified_at) },
                        ].map(({ fieldKey, value, variant }) => {
                          const label =
                            fieldKey === 'paid'
                              ? t('customerList.paidLabel')
                              : fieldKey === 'amountPaid'
                                ? t('customer.amountPaid')
                                : fieldKey === 'amountDue'
                                  ? t('customer.amountDue')
                                  : t(`customer.quickView.${fieldKey}`);
                          const isPaymentStatus = variant === 'paymentStatus' || fieldKey === 'paymentStatus';
                          return (
                            <Grid key={fieldKey} xs={12} sm={6}>
                              <Typography level="body2" sx={{ color: textSecondary, fontWeight: 600, mb: 0.25 }}>
                                {label}
                              </Typography>
                              {isPaymentStatus && quickViewInvoice.payment_status ? (
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  sx={{
                                    mt: 0.25,
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    ...getPaymentStatusChipStyle(quickViewInvoice.payment_status),
                                  }}
                                >
                                  {formatPaymentStatus(quickViewInvoice.payment_status)}
                                </Chip>
                              ) : (
                                <Typography level="body1" sx={{ color: textPrimary }}>
                                  {value}
                                </Typography>
                              )}
                            </Grid>
                          );
                        })}
                      </Grid>
                      {quickViewInvoice.shipments && quickViewInvoice.shipments.length > 0 && (
                        <>
                          <Typography level="title-sm" sx={{ color: textSecondary, fontWeight: 600, mb: 1, mt: 1 }}>
                            {t('customer.shipments')} ({quickViewInvoice.shipments.length})
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {quickViewInvoice.shipments.map((s) => (
                              <Box
                                key={s.id}
                                sx={{
                                  p: 1.5,
                                  borderRadius: '12px',
                                  bgcolor: '#f8fafc',
                                  border: '1px solid #e2e8f0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <LocalShippingIcon sx={{ color: textSecondary, fontSize: 20 }} />
                                  <Typography level="body2" sx={{ fontWeight: 600, color: textPrimary }}>
                                    {t('customer.shipmentNumber', { id: s.id })}
                                  </Typography>
                                  {s.tracking_number && (
                                    <Typography level="body2" sx={{ color: textSecondary }}>
                                      {s.tracking_number}
                                    </Typography>
                                  )}
                                  {isCustomerShipmentUnitFieldVisible('serial_number', unitVisibleFields) && getCustomerShipmentUnitSerial(s) && (
                                    <Typography level="body2" sx={{ color: textSecondary }}>
                                      {t('customer.unit')} {getCustomerShipmentUnitSerial(s)}
                                    </Typography>
                                  )}
                                  {isCustomerShipmentUnitFieldVisible('expected_warehouse_eta', unitVisibleFields) && getCustomerShipmentUnit(s)?.expected_warehouse_eta && (
                                    <Typography level="body2" sx={{ color: textSecondary, fontSize: '0.75rem' }}>
                                      {t('customer.quickView.expectedWarehouseEta')}: {formatCustomerShipmentUnitDate(getCustomerShipmentUnit(s).expected_warehouse_eta)}
                                    </Typography>
                                  )}
                                  {s.price != null && s.price !== '' && (
                                    <Typography level="body2" sx={{ color: textSecondary }}>
                                      <OmrAmount value={s.price} />
                                    </Typography>
                                  )}
                                </Box>
                                <Button
                                  variant="soft"
                                  size="sm"
                                  color="primary"
                                  startDecorator={<VisibilityIcon />}
                                  onClick={() => {
                                    setQuickViewInvoice(null);
                                    navigate(`/customer-shipment/${s.id}/tracking`);
                                  }}
                                  sx={{ borderRadius: '8px', fontWeight: 500 }}
                                >
                                  {t('customer.track')}
                                </Button>
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}
                      {quickViewInvoice.payments && quickViewInvoice.payments.length > 0 && (
                        <>
                          <Typography level="title-sm" sx={{ color: textSecondary, fontWeight: 600, mb: 1, mt: 2 }}>
                            {t('customerList.paymentsSection')}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {quickViewInvoice.payments.map((p) => (
                              <Box
                                key={p.id}
                                sx={{
                                  py: 1,
                                  px: 1.5,
                                  borderRadius: '8px',
                                  bgcolor: '#f1f5f9',
                                  border: '1px solid #e2e8f0',
                                }}
                              >
                                <Typography level="body2" sx={{ color: textPrimary }}>
                                  <OmrAmount value={p.amount} /> — {p.payment_method || '—'} — {formatDateTime(p.transaction_date || p.created_at)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <Button variant="plain" onClick={() => setQuickViewInvoice(null)}>
                          {t('actions.close')}
                        </Button>
                        <CustomerInvoicePayActions
                          invoice={quickViewInvoice}
                          needsRegenerate={needsRegenerate}
                          regenerating={regeneratingIds.has(quickViewInvoice.id)}
                          onRegenerate={() => {
                            setOpenPaymentLinkAfterRefresh(quickViewInvoice.id);
                            handleRegenerate(quickViewInvoice.id);
                          }}
                          onAfterPayFull={async () => {
                            await fetchInvoices();
                            setQuickViewInvoice(null);
                          }}
                          size="md"
                          compact
                        />
                      </Box>
                    </>
                  )}
                </ModalDialog>
              </Modal>

              <Modal open={!!pdfInvoice} onClose={() => setPdfInvoice(null)}>
                <ModalDialog
                  sx={{
                    maxWidth: 960,
                    width: '100%',
                    maxHeight: '95vh',
                    overflow: 'auto',
                    p: 2,
                  }}
                >
                  <ModalClose />
                  <DialogTitle>{t('invoiceManager.invoicePdfModalTitle')}</DialogTitle>
                  <DialogContent sx={{ px: 0 }}>
                    {pdfInvoice ? <ShipmentInvoice invoiceData={pdfInvoice} /> : null}
                  </DialogContent>
                </ModalDialog>
              </Modal>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 3, flexWrap: 'wrap' }}>
                  <IconButton
                    variant="outlined"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    {i18n.dir() === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                  </IconButton>
                  <Typography level="body2" sx={{ color: textSecondary, px: 1 }}>
                    {t('customerList.pageOf', { page, total: totalPages })}
                  </Typography>
                  <IconButton
                    variant="outlined"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    {i18n.dir() === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      <Snackbar
        autoHideDuration={5000}
        open={!!(exportSuccessMessage || exportError)}
        variant="soft"
        color={exportError ? 'danger' : 'success'}
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setExportSuccessMessage(null);
          setExportError(null);
        }}
      >
        {exportError || exportSuccessMessage}
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={regenerateSnackbar.open}
        variant="soft"
        color={regenerateSnackbar.color}
        onClose={(event, reason) => {
          if (reason === 'clickaway') return;
          setRegenerateSnackbar((s) => ({ ...s, open: false }));
        }}
      >
        {regenerateSnackbar.message}
      </Snackbar>
    </Box>
  );
}
